import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  IconButton,
  Chip,
  TextField,
  Button,
  Tooltip,
  CircularProgress,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Receipt as ReceiptIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Image as ImageIcon,
  Visibility as VisibilityIcon,
  FilterList as FilterListIcon,
  Warning as WarningIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { paymentProofsApi } from '../../api/paymentProofs';
import { EmptyState } from '../../components/common/EmptyState';
import { ConfirmDialog } from '../../components/common/ConfirmDialog';
import { useToast } from '../../hooks/useToast';
import { PaymentProofDetail } from './components/PaymentProofDetail';

/** Kobra IA — mismo patrón que Seguimiento / Inbox */
const K = {
  navy: '#121826',
  blue: '#4185F1',
  green: '#6BD56F',
  orange: '#FF9800',
  border: 'rgba(18, 24, 38, 0.08)',
  inputBg: '#EEF1F8',
  surface: '#F4F6F9',
  pageBg: '#F8FAFC',
};

const kobraShell = {
  borderRadius: 2,
  boxShadow: '0 1px 4px rgba(18, 24, 38, 0.06)',
  border: `1px solid ${K.border}`,
  bgcolor: '#fff',
};

const kobraFieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 1.5,
    backgroundColor: K.inputBg,
    '& fieldset': { borderColor: 'rgba(18, 24, 38, 0.1)' },
    '&:hover fieldset': { borderColor: `${K.blue}55` },
    '&.Mui-focused fieldset': { borderColor: K.blue, borderWidth: 2 },
  },
};

const kobraFilterLabelSx = {
  fontWeight: 700,
  fontSize: '0.7rem',
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  color: alpha(K.navy, 0.55),
  mb: 0.5,
  display: 'block',
};

const kobraBtnPrimary = {
  textTransform: 'none',
  fontWeight: 700,
  borderRadius: 1.5,
  px: 2,
  '&&': {
    bgcolor: K.blue,
    color: '#fff',
    backgroundImage: 'none',
    boxShadow: `0 4px 14px ${alpha(K.blue, 0.35)}`,
    '&:hover': { bgcolor: '#3574d9', backgroundImage: 'none' },
  },
};

const kobraBtnOutlined = {
  textTransform: 'none',
  fontWeight: 600,
  borderRadius: 1.5,
  borderColor: K.border,
  color: K.navy,
  '&:hover': { borderColor: alpha(K.blue, 0.4), bgcolor: alpha(K.blue, 0.04) },
};

const getEstadoLabel = (estado) => {
  const labels = {
    pendiente: 'Pendiente',
    validado: 'Validado',
    rechazado: 'Rechazado',
  };
  return labels[estado] || estado;
};

const estadoChipSx = (estado) => {
  if (estado === 'pendiente') {
    return {
      fontWeight: 700,
      bgcolor: alpha(K.orange, 0.12),
      color: '#E65100',
      border: `1px solid ${alpha(K.orange, 0.35)}`,
    };
  }
  if (estado === 'validado') {
    return {
      fontWeight: 700,
      bgcolor: alpha(K.green, 0.15),
      color: '#2E7D32',
      border: `1px solid ${alpha(K.green, 0.35)}`,
    };
  }
  return {
    fontWeight: 700,
    bgcolor: alpha('#E53935', 0.1),
    color: '#C62828',
    border: `1px solid ${alpha('#E53935', 0.35)}`,
  };
};

// Component to load image with proper headers
const PaymentProofThumbnail = ({ proofId, onClick }) => {
  const [imageUrl, setImageUrl] = React.useState(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    const loadImage = async () => {
      try {
        const url = await paymentProofsApi.getPaymentProofImage(proofId);
        setImageUrl(url);
      } catch (error) {
        console.error('Error loading image:', error);
      } finally {
        setLoading(false);
      }
    };
    loadImage();
  }, [proofId]);

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: 200,
        mx: 'auto',
        aspectRatio: '3 / 4',
        flexShrink: 0,
        mb: 1,
        borderRadius: 1.5,
        overflow: 'hidden',
        backgroundColor: K.surface,
        border: `1px solid ${K.border}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
      }}
      onClick={onClick}
    >
      {loading ? (
        <CircularProgress size={20} />
      ) : imageUrl ? (
        <img
          src={imageUrl}
          alt="Comprobante"
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'contain',
            objectPosition: 'center',
            display: 'block',
          }}
          onError={(e) => {
            e.target.style.display = 'none';
            const parent = e.target.parentElement;
            if (parent) {
              parent.innerHTML = '<div style="display: flex; align-items: center; justify-content: center; width: 100%; height: 100%;"><ImageIcon /></div>';
            }
          }}
        />
      ) : (
        <ImageIcon sx={{ fontSize: 36, color: alpha(K.navy, 0.35) }} />
      )}
    </Box>
  );
};

export const PaymentProofs = () => {
  const [activeTab, setActiveTab] = useState(0); // 0: pending, 1: accepted, 2: refused
  const [filters, setFilters] = useState({
    id_cliente: '',
    id_prestamo: '',
    whatsapp_number: '',
    search: '',
    fecha_desde: '',
    fecha_hasta: '',
  });
  const [selectedProof, setSelectedProof] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, type: null, proof: null, rejectReason: '' });
  const [editedAmounts, setEditedAmounts] = useState({}); // Store edited amounts by proof ID
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  // Fetch all payment proofs (without estado filter) to show counts in tabs
  const filtersWithoutEstado = useMemo(() => {
    const { estado, ...rest } = filters;
    return rest;
  }, [filters]);
  
  const { data: allPaymentProofs, isLoading, refetch } = useQuery({
    queryKey: ['paymentProofs', filtersWithoutEstado],
    queryFn: () => paymentProofsApi.getPaymentProofs(filtersWithoutEstado),
  });

  const handleRefresh = () => {
    refetch();
    showToast('Datos actualizados', 'success');
  };

  // Filter by active tab
  const paymentProofs = useMemo(() => {
    if (!allPaymentProofs) return [];
    const estadoMap = {
      0: 'pendiente',
      1: 'validado',
      2: 'rechazado',
    };
    const estado = estadoMap[activeTab];
    return allPaymentProofs.filter(p => p.estado === estado);
  }, [allPaymentProofs, activeTab]);

  const validateMutation = useMutation({
    mutationFn: ({ id, data }) => paymentProofsApi.validatePaymentProof(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['paymentProofs'] });
      setDetailOpen(false);
      setConfirmDialog({ open: false, type: null, proof: null, rejectReason: '' });
      showToast('Comprobante validado exitosamente', 'success');
      // Switch to accepted tab after validation
      setActiveTab(1);
    },
    onError: (error) => {
      showToast(error.message || 'Error al validar comprobante', 'error');
    },
  });

  const rejectMutation = useMutation({
    mutationFn: ({ id, reason }) => paymentProofsApi.rejectPaymentProof(id, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['paymentProofs'] });
      setDetailOpen(false);
      setConfirmDialog({ open: false, type: null, proof: null, rejectReason: '' });
      showToast('Comprobante rechazado exitosamente', 'success');
      // Switch to rejected tab after rejection
      setActiveTab(2);
    },
    onError: (error) => {
      showToast(error.message || 'Error al rechazar comprobante', 'error');
    },
  });

  const handleViewDetails = async (proof) => {
    try {
      const details = await paymentProofsApi.getPaymentProof(proof.id);
      setSelectedProof(details);
      setDetailOpen(true);
    } catch (error) {
      showToast('Error al cargar detalles del comprobante', 'error');
    }
  };

  const handleValidate = (id, data) => {
    // If data is provided (from detail dialog), validate directly
    if (data !== undefined) {
      // Include edited amount if available
      const proof = allPaymentProofs?.find(p => p.id === id);
      if (proof && editedAmounts[id] !== undefined) {
        const extractedData = data.extracted_data || proof.extracted_data || {};
        data.extracted_data = {
          ...extractedData,
          monto: editedAmounts[id],
          amount: editedAmounts[id],
        };
      }
      validateMutation.mutate({ id, data });
      return;
    }
    // Otherwise, show confirmation dialog (from card button)
    const proof = allPaymentProofs?.find(p => p.id === id);
    if (proof) {
      // Don't allow validation if id_prestamo doesn't exist
      if (!proof.id_prestamo) {
        showToast('No se puede validar un comprobante sin ID de préstamo', 'warning');
        return;
      }
      setConfirmDialog({
        open: true,
        type: 'validate',
        proof: proof,
        rejectReason: '',
      });
    }
  };

  const handleReject = (id, reason) => {
    // If reason is provided (from detail dialog), reject directly
    if (reason && reason.trim()) {
      rejectMutation.mutate({ id, reason });
      return;
    }
    // Otherwise, open dialog to get reason (from card button)
    const proof = allPaymentProofs?.find(p => p.id === id);
    if (proof) {
      setConfirmDialog({
        open: true,
        type: 'reject',
        proof: proof,
        rejectReason: '',
      });
    }
  };

  const handleConfirmValidate = () => {
    // Prevent double-click: don't allow if mutation is already pending
    if (validateMutation.isPending) {
      return;
    }
    
    if (confirmDialog.proof) {
      // Don't allow validation if id_prestamo doesn't exist
      if (!confirmDialog.proof.id_prestamo) {
        showToast('No se puede validar un comprobante sin ID de préstamo', 'warning');
        setConfirmDialog({ open: false, type: null, proof: null, rejectReason: '' });
        return;
      }
      // Get edited amount if available
      const extractedData = confirmDialog.proof.extracted_data || {};
      const originalAmount = extractedData.monto || extractedData.amount || 0;
      const editedAmount = editedAmounts[confirmDialog.proof.id];
      const finalAmount = editedAmount !== undefined ? editedAmount : originalAmount;
      
      // Update extracted_data with edited amount
      const updatedExtractedData = {
        ...extractedData,
        monto: finalAmount,
        amount: finalAmount,
      };
      
      validateMutation.mutate({ 
        id: confirmDialog.proof.id, 
        data: { 
          observaciones: confirmDialog.proof.observaciones || '',
          extracted_data: updatedExtractedData
        } 
      });
    }
  };

  const handleConfirmReject = () => {
    if (confirmDialog.proof && confirmDialog.rejectReason.trim()) {
      rejectMutation.mutate({ 
        id: confirmDialog.proof.id, 
        reason: confirmDialog.rejectReason 
      });
    } else {
      showToast('Por favor ingrese un motivo para rechazar el comprobante', 'warning');
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    // Don't update filters.estado since we filter client-side
  };

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const clearFilters = () => {
    setActiveTab(0); // Reset to pending tab
    setFilters({
      id_cliente: '',
      id_prestamo: '',
      whatsapp_number: '',
      search: '',
      fecha_desde: '',
      fecha_hasta: '',
    });
  };

  const filteredCount = useMemo(() => {
    return paymentProofs?.length || 0;
  }, [paymentProofs]);

  if (isLoading) {
    return (
      <Box
        sx={{
          p: 3,
          width: '100%',
          maxWidth: { sm: '100%', md: '1700px' },
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '400px',
          bgcolor: K.pageBg,
          borderRadius: 2,
        }}
      >
        <CircularProgress sx={{ color: K.blue }} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        p: { xs: 2, md: 3 },
        width: '100%',
        maxWidth: { sm: '100%', md: '1700px' },
        height: '100vh',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: K.pageBg,
        borderRadius: 2,
      }}
    >
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap', flexShrink: 0 }}>
        <Box
          sx={{
            p: 1.5,
            borderRadius: 2,
            bgcolor: alpha(K.blue, 0.12),
            color: K.blue,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <ReceiptIcon sx={{ fontSize: 32 }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 200 }}>
          <Typography variant="caption" sx={{ display: 'block', letterSpacing: '0.06em', color: alpha(K.navy, 0.45), mb: 0.75, fontWeight: 600 }}>
            Cobranzas · Comprobantes
          </Typography>
          <Typography variant="h5" sx={{ fontWeight: 800, color: K.navy, letterSpacing: '-0.02em', mb: 0.5 }}>
            Comprobantes de transferencia
          </Typography>
          <Typography variant="body2" sx={{ color: alpha(K.navy, 0.55) }}>
            Gestiona y valida comprobantes de pago recibidos por WhatsApp
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={handleRefresh} disabled={isLoading} sx={kobraBtnOutlined}>
            Actualizar
          </Button>
          <Button
            variant="outlined"
            startIcon={<FilterListIcon />}
            onClick={() => setShowFilters(!showFilters)}
            sx={kobraBtnOutlined}
          >
            {showFilters ? 'Ocultar filtros' : 'Filtros'}
          </Button>
        </Box>
      </Box>

      {/* Tabs */}
      <Paper
        elevation={0}
        sx={{
          ...kobraShell,
          mb: 2,
          flexShrink: 0,
          px: 1,
        }}
      >
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          aria-label="payment proof status tabs"
          sx={{
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 700,
              color: alpha(K.navy, 0.45),
              minHeight: 48,
            },
            '& .Mui-selected': {
              color: `${K.blue} !important`,
            },
            '& .MuiTabs-indicator': {
              backgroundColor: K.blue,
              height: 3,
              borderRadius: '3px 3px 0 0',
            },
          }}
        >
          <Tab
            label={`Pendientes${allPaymentProofs ? ` (${allPaymentProofs.filter((p) => p.estado === 'pendiente').length})` : ''}`}
          />
          <Tab
            label={`Validados${allPaymentProofs ? ` (${allPaymentProofs.filter((p) => p.estado === 'validado').length})` : ''}`}
          />
          <Tab
            label={`Rechazados${allPaymentProofs ? ` (${allPaymentProofs.filter((p) => p.estado === 'rechazado').length})` : ''}`}
          />
        </Tabs>
      </Paper>

      {/* Filters */}
      {showFilters && (
        <Card sx={{ ...kobraShell, mb: 2, p: { xs: 2, md: 2.5 }, flexShrink: 0 }}>
          <Typography sx={{ ...kobraFilterLabelSx, mb: 2 }}>Filtros</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <Typography sx={kobraFilterLabelSx}>WhatsApp</Typography>
              <TextField
                fullWidth
                size="small"
                value={filters.whatsapp_number}
                onChange={(e) => handleFilterChange('whatsapp_number', e.target.value)}
                placeholder="Ej: 1234567890"
                sx={kobraFieldSx}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography sx={kobraFilterLabelSx}>ID cliente</Typography>
              <TextField
                fullWidth
                size="small"
                value={filters.id_cliente}
                onChange={(e) => handleFilterChange('id_cliente', e.target.value)}
                placeholder="ID del cliente"
                sx={kobraFieldSx}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography sx={kobraFilterLabelSx}>ID préstamo</Typography>
              <TextField
                fullWidth
                size="small"
                value={filters.id_prestamo}
                onChange={(e) => handleFilterChange('id_prestamo', e.target.value)}
                placeholder="ID del préstamo"
                sx={kobraFieldSx}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Typography sx={kobraFilterLabelSx}>Búsqueda</Typography>
              <TextField
                fullWidth
                size="small"
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                placeholder="Nombre, ID, número…"
                sx={kobraFieldSx}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Typography sx={kobraFilterLabelSx}>Fecha desde</Typography>
              <TextField
                fullWidth
                size="small"
                type="date"
                value={filters.fecha_desde}
                onChange={(e) => handleFilterChange('fecha_desde', e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={kobraFieldSx}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Typography sx={kobraFilterLabelSx}>Fecha hasta</Typography>
              <TextField
                fullWidth
                size="small"
                type="date"
                value={filters.fecha_hasta}
                onChange={(e) => handleFilterChange('fecha_hasta', e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={kobraFieldSx}
              />
            </Grid>
            <Grid item xs={12}>
              <Button
                variant="text"
                onClick={clearFilters}
                sx={{ color: K.blue, fontWeight: 700, textTransform: 'none', '&:hover': { bgcolor: alpha(K.blue, 0.06) } }}
              >
                Limpiar filtros
              </Button>
            </Grid>
          </Grid>
        </Card>
      )}

      {/* Results count */}
      {paymentProofs && (
        <Box sx={{ mb: 2, flexShrink: 0, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Typography variant="body2" sx={{ color: alpha(K.navy, 0.55) }}>
            {filteredCount} comprobante{filteredCount !== 1 ? 's' : ''} encontrado{filteredCount !== 1 ? 's' : ''}
          </Typography>
          <Chip
            size="small"
            label={`Tab ${activeTab === 0 ? 'Pendientes' : activeTab === 1 ? 'Validados' : 'Rechazados'}`}
            sx={{ fontWeight: 700, bgcolor: alpha(K.blue, 0.1), color: K.blue, border: `1px solid ${alpha(K.blue, 0.2)}` }}
          />
        </Box>
      )}

      {/* Payment Proofs Grid */}
      <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', pb: 12 }}>
      {!paymentProofs?.length ? (
        <EmptyState
          icon={ReceiptIcon}
          title="No hay Comprobantes"
          description="No se encontraron comprobantes con los filtros seleccionados"
        />
      ) : (
        <Grid container spacing={2} alignItems="stretch">
          {paymentProofs.map((proof) => {
            const extractedData = proof.extracted_data || {};
            const monto = extractedData.monto || extractedData.amount || null;
            const fecha = extractedData.fecha || extractedData.date || null;
            const referencia = extractedData.referencia || extractedData.reference || null;
            const banco = extractedData.banco || extractedData.bank || null;
            const editingConfidence = extractedData.editing_confidence || null;
            const hasHighEditingConfidence = editingConfidence !== null && editingConfidence >= 0.9;
            const isDuplicate = proof.observaciones && proof.observaciones.includes('Ya existe un comprobante con los mismos datos');
            const hasWarning = hasHighEditingConfidence || isDuplicate;

            return (
              <Grid item size={4} key={proof.id} sx={{ display: 'flex', minWidth: 0 }}>
                <Card
                  sx={{
                    width: '100%',
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    height: { xs: 'auto', sm: 520 },
                    maxHeight: { xs: 'none', sm: 520 },
                    ...kobraShell,
                    transition: 'box-shadow 0.2s, transform 0.2s',
                    border: hasWarning ? `2px solid ${alpha('#E53935', 0.55)}` : `1px solid ${K.border}`,
                    borderLeft: hasWarning ? `4px solid ${alpha('#E53935', 0.9)}` : `4px solid ${K.blue}`,
                    '&:hover': {
                      boxShadow: `0 8px 24px ${alpha(K.navy, 0.08)}`,
                      transform: 'translateY(-2px)',
                    },
                  }}
                >
                  <CardContent
                    sx={{
                      flex: 1,
                      display: 'flex',
                      flexDirection: 'column',
                      minHeight: 0,
                      overflow: 'hidden',
                      p: 2,
                      '&:last-child': { pb: 2 },
                    }}
                  >
                    {/* High Editing Confidence Warning Banner */}
                    {hasHighEditingConfidence && (
                      <Box
                        sx={{
                          mb: 1,
                          p: 1,
                          bgcolor: alpha('#E53935', 0.1),
                          color: '#C62828',
                          borderRadius: 1.5,
                          border: `1px solid ${alpha('#E53935', 0.35)}`,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.75,
                          flexShrink: 0,
                        }}
                      >
                        <WarningIcon sx={{ fontSize: 18 }} />
                        <Typography variant="caption" sx={{ fontWeight: 700, flex: 1, lineHeight: 1.3 }}>
                          Imagen posiblemente editada ({Math.round(editingConfidence * 100)}%)
                        </Typography>
                      </Box>
                    )}

                    {isDuplicate && (
                      <Box
                        sx={{
                          mb: 1,
                          p: 1,
                          bgcolor: alpha('#E53935', 0.1),
                          color: '#C62828',
                          borderRadius: 1.5,
                          border: `1px solid ${alpha('#E53935', 0.35)}`,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.75,
                          flexShrink: 0,
                        }}
                      >
                        <WarningIcon sx={{ fontSize: 18 }} />
                        <Typography variant="caption" sx={{ fontWeight: 700, flex: 1, lineHeight: 1.3 }}>
                          Comprobante duplicado detectado
                        </Typography>
                      </Box>
                    )}
                    
                    {/* Header */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5, flexShrink: 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                        <Box
                          sx={{
                            p: 1,
                            borderRadius: 1.5,
                            bgcolor: hasWarning ? alpha('#E53935', 0.12) : alpha(K.blue, 0.12),
                            color: hasWarning ? '#C62828' : K.blue,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <ReceiptIcon sx={{ fontSize: 22 }} />
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="caption" sx={{ color: alpha(K.navy, 0.45), fontWeight: 600, fontSize: '0.7rem' }}>
                            {proof.created_at ? new Date(proof.created_at).toLocaleDateString('es-ES') : 'N/A'}
                          </Typography>
                        </Box>
                      </Box>
                      <Chip label={getEstadoLabel(proof.estado)} size="small" sx={estadoChipSx(proof.estado)} />
                    </Box>

                    <Box
                      sx={{
                        flex: 1,
                        minHeight: 0,
                        overflowY: 'auto',
                        pr: 0.5,
                        mb: 1,
                      }}
                    >
                    <Grid container spacing={1.5}>
                      <Grid item size={6}>
                        {/* Thumbnail */}
                        {proof.id && (
                          <PaymentProofThumbnail proofId={proof.id} onClick={() => handleViewDetails(proof)} />
                        )}

                      </Grid>
                      <Grid item size={6}>
                    {/* Info Section */}
                    <Box sx={{ mb: 0 }}>
                      {proof.cliente && (
                        <Box sx={{ mb: 1 }}>
                          <Typography sx={{ ...kobraFilterLabelSx, fontSize: '0.65rem' }}>Cliente</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: K.navy, fontSize: '0.8rem' }}>
                            {proof.id_cliente || 'N/A'}
                          </Typography>
                          {proof.cliente.identificacion && (
                            <Typography variant="caption" sx={{ color: alpha(K.navy, 0.45) }}>
                              ID: {proof.cliente.identificacion}
                            </Typography>
                          )}
                        </Box>
                      )}
                      {proof.id_prestamo && (
                        <Box sx={{ mb: 1 }}>
                          <Typography sx={{ ...kobraFilterLabelSx, fontSize: '0.65rem' }}>Préstamo</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: K.navy, fontSize: '0.8rem' }}>
                            {proof.id_prestamo || 'N/A'}
                          </Typography>
                        </Box>
                      )}
                      {proof.whatsapp_number && (
                        <Box sx={{ mb: 1 }}>
                          <Typography sx={{ ...kobraFilterLabelSx, fontSize: '0.65rem' }}>WhatsApp</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: K.navy, fontSize: '0.8rem' }}>
                            {proof.whatsapp_number}
                          </Typography>
                        </Box>
                      )}
                      {monto && (
                        <Box sx={{ mb: 0.75 }}>
                          <Typography sx={{ ...kobraFilterLabelSx, fontSize: '0.65rem' }}>Monto</Typography>
                          {proof.estado === 'pendiente' ? (
                            <TextField
                              type="number"
                              size="small"
                              fullWidth
                              value={editedAmounts[proof.id] !== undefined ? editedAmounts[proof.id] : monto}
                              onChange={(e) => {
                                const newAmount = parseFloat(e.target.value) || 0;
                                setEditedAmounts(prev => ({
                                  ...prev,
                                  [proof.id]: newAmount
                                }));
                              }}
                              inputProps={{
                                step: 0.01,
                                min: 0,
                              }}
                              sx={{
                                mt: 0.5,
                                ...kobraFieldSx,
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: 1.5,
                                  backgroundColor: K.inputBg,
                                  fontSize: '0.875rem',
                                  fontWeight: 600,
                                  color: '#2E7D32',
                                  '& fieldset': { borderColor: 'rgba(18, 24, 38, 0.1)' },
                                  '&:hover fieldset': { borderColor: `${K.green}55` },
                                  '&.Mui-focused fieldset': { borderColor: K.green, borderWidth: 2 },
                                },
                              }}
                              InputProps={{
                                startAdornment: <Typography sx={{ mr: 0.5, color: alpha(K.navy, 0.45) }}>$</Typography>,
                              }}
                            />
                          ) : (
                            <Typography variant="body2" sx={{ fontWeight: 700, color: '#2E7D32', fontSize: '0.8rem' }}>
                              ${parseFloat(monto).toLocaleString('es-ES', { minimumFractionDigits: 2 })}
                            </Typography>
                          )}
                        </Box>
                      )}
                      {proof.confidence !== null && proof.confidence !== undefined && (
                        <Box sx={{ mb: 0.75 }}>
                          <Typography sx={{ ...kobraFilterLabelSx, fontSize: '0.65rem' }}>Confianza</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: K.navy, fontSize: '0.8rem' }}>
                            {(proof.confidence * 100).toFixed(0)}%
                          </Typography>
                        </Box>
                      )}
                    </Box>
                        
                      </Grid>
                    </Grid>
                    </Box>

                    {/* Actions */}
                    <Box
                      sx={{
                        display: 'flex',
                        gap: 0.5,
                        justifyContent: 'flex-end',
                        pt: 1,
                        mt: 'auto',
                        flexShrink: 0,
                        borderTop: `1px solid ${K.border}`,
                      }}
                    >
                      <Tooltip title="Ver detalles">
                        <IconButton
                          size="small"
                          onClick={() => handleViewDetails(proof)}
                          sx={{
                            color: K.blue,
                            '&:hover': { bgcolor: alpha(K.blue, 0.08) },
                          }}
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {proof.estado === 'pendiente' && (
                        <>
                          <Tooltip title={proof.id_prestamo ? 'Validar' : 'No se puede validar sin ID de préstamo'}>
                            <IconButton
                              size="small"
                              onClick={() => handleValidate(proof.id)}
                              disabled={validateMutation.isPending || !proof.id_prestamo}
                              sx={{
                                color: K.green,
                                '&:hover': { bgcolor: alpha(K.green, 0.15) },
                                '&:disabled': { color: alpha(K.navy, 0.26) },
                              }}
                            >
                              <CheckCircleIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Rechazar">
                            <IconButton
                              size="small"
                              onClick={() => handleReject(proof.id)}
                              disabled={rejectMutation.isPending}
                              sx={{
                                color: '#C62828',
                                '&:hover': { bgcolor: alpha('#E53935', 0.08) },
                              }}
                            >
                              <CancelIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}
      </Box>

      {/* Detail Dialog */}
      {selectedProof && (
        <PaymentProofDetail
          open={detailOpen}
          onClose={() => {
            setDetailOpen(false);
            setSelectedProof(null);
          }}
          proof={selectedProof}
          onValidate={handleValidate}
          onReject={handleReject}
          isLoading={validateMutation.isPending || rejectMutation.isPending}
        />
      )}

      {/* Confirm Dialog */}
      <ConfirmDialog
        open={confirmDialog.open && confirmDialog.type === 'validate'}
        title="Validar Comprobante"
        message={`¿Está seguro que desea validar este comprobante? Esta acción no se puede deshacer.`}
        onConfirm={handleConfirmValidate}
        onCancel={() => setConfirmDialog({ open: false, type: null, proof: null, rejectReason: '' })}
        confirmLabel="Validar"
        cancelLabel="Cancelar"
        disabled={validateMutation.isPending}
      />

      {/* Reject Dialog with reason input */}
      <Dialog
        open={confirmDialog.open && confirmDialog.type === 'reject'}
        onClose={() => setConfirmDialog({ open: false, type: null, proof: null, rejectReason: '' })}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2, border: `1px solid ${K.border}` } }}
      >
        <DialogTitle sx={{ fontWeight: 800, color: K.navy }}>Rechazar comprobante</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2, color: alpha(K.navy, 0.65) }}>
            ¿Está seguro que desea rechazar este comprobante? Esta acción no se puede deshacer.
          </Typography>
          <Typography sx={{ ...kobraFilterLabelSx, mb: 1 }}>Motivo del rechazo</Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            value={confirmDialog.rejectReason}
            onChange={(e) => setConfirmDialog((prev) => ({ ...prev, rejectReason: e.target.value }))}
            placeholder="Ingrese el motivo del rechazo…"
            required
            sx={kobraFieldSx}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button
            onClick={() => setConfirmDialog({ open: false, type: null, proof: null, rejectReason: '' })}
            sx={kobraBtnOutlined}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleConfirmReject}
            variant="contained"
            disabled={!confirmDialog.rejectReason.trim() || rejectMutation.isPending}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              borderRadius: 1.5,
              bgcolor: '#C62828',
              '&:hover': { bgcolor: '#b71c1c' },
            }}
          >
            {rejectMutation.isPending ? 'Rechazando…' : 'Rechazar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

